import React, { useState, useRef, useEffect } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCamera, faStop, faImage, faSpinner, faTimes, faCheck, faUpload } from '@fortawesome/pro-solid-svg-icons'
import { Dispatch } from '../architecture/frontend/index'

const SimpleScanModal = (props) => {
  // Add error boundary handling
  const [hasError, setHasError] = useState(false)
  const [isStreaming, setIsStreaming] = useState(false)
  const [isStartingCamera, setIsStartingCamera] = useState(false)
  const [capturedImages, setCapturedImages] = useState([])
  const [error, setError] = useState('')
  const [isCapturing, setIsCapturing] = useState(false)
  const [showFlash, setShowFlash] = useState(false)
  const [justCaptured, setJustCaptured] = useState(false)
  
  const videoRef = useRef(null)
  const canvasRef = useRef(null)
  const streamRef = useRef(null)

  // Get processed images from state
  const processedImages = props.processedImages || []
  const isProcessing = props.isProcessing || false

  // Add error boundary
  useEffect(() => {
    const handleError = (error) => {
      console.error('SimpleScan Component Error:', error)
      setHasError(true)
      setError('Component error occurred. Please refresh and try again.')
    }

    window.addEventListener('error', handleError)
    return () => window.removeEventListener('error', handleError)
  }, [])

  // Monitor processing status changes
  useEffect(() => {
    // Listen for processing updates
    const subscription = Dispatch.getAction('Response.processImage')
      .subscribe((response) => {
        if (response.data && response.data.processImage) {
          console.log('Image processing response:', response.data.processImage)
        }
        if (response.errors) {
          console.error('Image processing error:', response.errors)
          setError('Failed to process image. Please try again.')
        }
      })

    return () => subscription.unsubscribe()
  }, [])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop())
      }
    }
  }, [])

  const startCamera = async () => {
    try {
      setError('')
      setIsStartingCamera(true)
      
      setIsStreaming(true)
      await new Promise(resolve => setTimeout(resolve, 100))
      
      if (!videoRef.current) {
        console.error('Video element still not available after waiting')
        setError('Video element not found. Please try again.')
        setIsStartingCamera(false)
        setIsStreaming(false)
        return
      }
      
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: 'environment',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        } 
      })
      
      console.log('Setting up video stream...', stream)
      videoRef.current.srcObject = stream
      streamRef.current = stream
      
      videoRef.current.onloadedmetadata = () => {
        console.log('Video metadata loaded, starting playback...')
        videoRef.current.play().then(() => {
          console.log('Video playback started successfully')
          setIsStartingCamera(false)
        }).catch(err => {
          console.error('Error playing video:', err)
          setError('Unable to start video playback.')
          setIsStartingCamera(false)
          setIsStreaming(false)
        })
      }
      
      videoRef.current.onerror = (err) => {
        console.error('Video element error:', err)
        setError('Video playback error occurred.')
        setIsStartingCamera(false)
        setIsStreaming(false)
      }
      
    } catch (err) {
      setError('Unable to access camera. Please ensure you have granted camera permissions.')
      console.error('Camera access error:', err)
      setIsStartingCamera(false)
      setIsStreaming(false)
    }
  }

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop())
      streamRef.current = null
    }
    setIsStreaming(false)
  }

  const dataURLtoFile = (dataurl, filename) => {
    const arr = dataurl.split(',')
    const mime = arr[0].match(/:(.*?);/)[1]
    const bstr = atob(arr[1])
    let n = bstr.length
    const u8arr = new Uint8Array(n)
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n)
    }
    return new File([u8arr], filename, { type: mime })
  }

  const captureImage = async () => {
    console.log('captureImage called')
    if (!videoRef.current || !canvasRef.current) {
      console.error('Video or canvas ref not available')
      return
    }

    // Set capturing state to disable button and show feedback
    setIsCapturing(true)
    
    // Show flash effect
    setShowFlash(true)
  

    const video = videoRef.current
    const canvas = canvasRef.current
    const context = canvas.getContext('2d')

    console.log('Video dimensions:', video.videoWidth, video.videoHeight)

    canvas.width = video.videoWidth
    canvas.height = video.videoHeight

    context.drawImage(video, 0, 0, canvas.width, canvas.height)

    const imageDataUrl = canvas.toDataURL('image/jpeg', 0.8)
    console.log('Image captured, data URL length:', imageDataUrl.length)
    
    // Add to captured images for display
    const imageId = Date.now()
    const newImage = {
      id: imageId,
      dataUrl: imageDataUrl,
      timestamp: new Date().toISOString(),
      status: 'captured'
    }
    setCapturedImages(prev => [...prev, newImage])
    
    // Convert to file and send for processing
    const file = dataURLtoFile(imageDataUrl, `scan_${imageId}.jpg`)
    Dispatch.nextAction('processImage.process', {
      file: file
    })
    
    console.log('Image added to upload queue')
    
    // Show success feedback
    setJustCaptured(true)
    
    // Hide flash effect after a brief moment
    setTimeout(() => {
      setShowFlash(false)
    }, 150)
    
    // Reset capturing state and success feedback
    setTimeout(() => {
      setIsCapturing(false)
      setJustCaptured(false)
    }, 800)
  }

  const closeModal = () => {
    stopCamera()
    Dispatch.nextAction('__.Modal.close')
  }

  const clearAllImages = () => {
    setCapturedImages([])
    setError('')
    // Clear processed images from state if needed
    Dispatch.nextAction('processedImages.clear')
  }

  // Early return for error state
  if (hasError) {
    return (
      <div className='w-100 avenir dark-gray'>
        <div className='w-100 pa3 flex items-center justify-between bg-red'>
          <div className='flex items-center'>
            <FontAwesomeIcon className='f6 white' icon={faTimes} />
            <div className='pl2 fw7 f6 white'>Component Error</div>
          </div>
          <div className='fw7 f5 white pointer hover-light-gray'
            onClick={closeModal}
          >
            <FontAwesomeIcon icon={faTimes} />
          </div>
        </div>
        <div className='pa4'>
          <div className='pa3 bg-light-red ba b--red br2'>
            <div className='red fw6'>Error:</div>
            <div className='red'>{error}</div>
            <div className='pt2'>
              <button 
                className='bg-evonik-purple white pa2 ph3 br3 fw6 pointer hover-bg-evonik-light-purple bn f6'
                onClick={closeModal}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='w-100 avenir dark-gray'>
      <style>
        {`
          @keyframes flashEffect {
            0% { opacity: 0; }
            50% { opacity: 1; }
            100% { opacity: 0; }
          }
          
          @keyframes successPulse {
            0% { opacity: 0; transform: scale(0.8); }
            30% { opacity: 1; transform: scale(1.1); }
            70% { opacity: 1; transform: scale(1); }
            100% { opacity: 0; transform: scale(1); }
          }
          
          @keyframes checkmarkPop {
            0% { opacity: 0; transform: scale(0.5); }
            30% { opacity: 1; transform: scale(1.3); }
            70% { opacity: 1; transform: scale(1); }
            100% { opacity: 0; transform: scale(1); }
          }
          
          .transition-all {
            transition: all 0.15s ease;
          }
        `}
      </style>
      <div className='w-100 pa3 flex items-center justify-between bg-evonik-purple'>
        <div className='flex items-center'>
          <FontAwesomeIcon className='f6 white' icon={faCamera} />
          <div className='pl2 fw7 f6 white'>Multi-Image Scanner</div>
        </div>
        <div className='fw7 f5 white pointer hover-light-gray'
          onClick={closeModal}
        >
          <FontAwesomeIcon icon={faTimes} />
        </div>
      </div>

      <div className='pa4 pb5'>
        {error && (
          <div className='mb3 pa3 bg-light-red ba b--red br2'>
            <div className='red fw6'>Error:</div>
            <div className='red'>{error}</div>
          </div>
        )}

        {/* Camera controls */}
        {!isStreaming && !isStartingCamera && (
          <div className='tc'>
            <div className='mb4'>
              <h2 className='evonik-purple fw6'>Scan Multiple Images</h2>
              <p className='gray fw4'>
                Capture multiple images quickly. Each image will be automatically uploaded and processed in the background.
              </p>
            </div>
            <button 
              className='bg-evonik-purple white pa3 ph4 br3 fw6 pointer hover-bg-evonik-light-purple bn f5'
              onClick={startCamera}
            >
              <FontAwesomeIcon className='pr2' icon={faCamera} />
              Open Camera
            </button>
          </div>
        )}

        {/* Camera view */}
        {isStreaming && (
          <div className='tc'>
            <div className='mb3 relative'>
              <video 
                ref={videoRef}
                autoPlay 
                playsInline
                muted
                className='w-100 br3 shadow-4'
                style={{
                  maxWidth: '500px', 
                  maxHeight: '400px',
                  minHeight: '300px',
                  backgroundColor: '#000',
                  objectFit: 'cover'
                }}
              />
              {/* Flash overlay */}
              {showFlash && (
                <div 
                  className='absolute top-0 left-0 w-100 h-100 br3'
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    animation: 'flashEffect 0.15s ease-out',
                    pointerEvents: 'none'
                  }}
                />
              )}
              {/* Success indicator */}
              {justCaptured && (
                <div 
                  className='absolute top-0 left-0 w-100 h-100 br3 flex items-center justify-center'
                  style={{
                    backgroundColor: 'rgba(0, 200, 0, 0.3)',
                    animation: 'successPulse 0.6s ease-out',
                    pointerEvents: 'none'
                  }}
                >
                  <FontAwesomeIcon 
                    className='f1 white'
                    icon={faCheck}
                    style={{
                      textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
                      animation: 'checkmarkPop 0.6s ease-out'
                    }}
                  />
                </div>
              )}
            </div>
            {isStartingCamera ? (
              <div className='mb3 pa3 bg-light-blue ba b--blue br2'>
                <FontAwesomeIcon className='blue pr2' icon={faSpinner} spin />
                <span className='blue fw6'>Setting up camera...</span>
              </div>
            ) : (
              <div className='flex justify-center gap2'>
                <button 
                  className={`${isCapturing ? 'bg-orange' : (justCaptured ? 'bg-dark-green' : 'bg-green')} white pa3 ph4 br3 fw6 pointer ${!isCapturing && !justCaptured ? 'hover-bg-dark-green' : ''} bn f5 mr2 transition-all`}
                  onClick={captureImage}
                  disabled={isProcessing || isCapturing}
                  style={{
                    transform: isCapturing ? 'scale(0.95)' : (justCaptured ? 'scale(1.05)' : 'scale(1)'),
                    transition: 'all 0.15s ease'
                  }}
                >
                  <FontAwesomeIcon 
                    className='pr2' 
                    icon={isCapturing ? faSpinner : (justCaptured ? faCheck : faImage)}
                    spin={isCapturing}
                  />
                  {isCapturing ? 'Capturing...' : (justCaptured ? 'Captured!' : 'Capture Image')}
                </button>
                <button 
                  className='bg-red white pa3 ph4 br3 fw6 pointer hover-bg-dark-red bn f5'
                  onClick={stopCamera}
                >
                  <FontAwesomeIcon className='pr2' icon={faStop} />
                  Stop Camera
                </button>
              </div>
            )}
          </div>
        )}


        {/* Hidden canvas for image capture */}
        <canvas ref={canvasRef} style={{display: 'none'}} />
      </div>
    </div>
  )
}

export default SimpleScanModal
